import { useCallback, useEffect, useState } from "react";
import { api } from "@/api";
import type { CoachInfo, PlannedWorkout } from "@/lib/types";
import { useUI } from "@/lib/theme";
import DirectChat from "./DirectChat";
import { DemoCoachData } from "@/lib/demoData";

const LS_SEEN_KEY = "westive.coachSeenAt";

type Step = "idle" | "previewing" | "consenting" | "done";
type CoachTab = "msg" | "updates";

export default function MyCoach({
  onNavigateCalendar,
  onUnseenCount,
  demo,
  coachData,
}: {
  onNavigateCalendar?: () => void;
  onUnseenCount?: (n: number) => void;
  demo?: boolean;
  coachData?: DemoCoachData;
}) {
  const { t, lang } = useUI();
  const [coach, setCoach] = useState<CoachInfo | null | undefined>(undefined);
  const [hasShareableSource, setHasShareableSource] = useState<boolean | null>(null);
  const [code, setCode] = useState("");
  const [step, setStep] = useState<Step>("idle");
  const [coachName, setCoachName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [working, setWorking] = useState(false);
  const [coachTab, setCoachTab] = useState<CoachTab>("msg");
  const [upcomingPlans, setUpcomingPlans] = useState<PlannedWorkout[]>([]);

  const load = useCallback(() => {
    if (demo && coachData) {
      setCoach({ name: coachData.coachName, profilePic: coachData.coachAvatar });
      setHasShareableSource(true);
      return;
    }
    api.coachLink().then((r) => setCoach(r.coach)).catch(() => setCoach(null));
    api.connections()
      .then((r) => {
        const shareable = r.catalog.some(
          (c) => c.isConnected && c.tos.thirdPartyDisplay !== "forbidden",
        );
        setHasShareableSource(shareable);
      })
      .catch(() => setHasShareableSource(null));
  }, [demo, coachData]);

  useEffect(() => { load(); }, [load]);

  // Al montar (drawer abierto): cargar planes próximos, calcular badge y marcar como vistos.
  useEffect(() => {
    const seenAt = parseInt(localStorage.getItem(LS_SEEN_KEY) ?? "0", 10);
    const today = new Date().toISOString().slice(0, 10);
    const future = new Date(Date.now() + 30 * 86400 * 1000).toISOString().slice(0, 10);

    if (demo && coachData) {
      const demoWorkouts = coachData.plannedWorkouts.filter((w) => w.scheduled_date >= today && w.scheduled_date <= future);
      setUpcomingPlans(demoWorkouts);
      if (onUnseenCount) onUnseenCount(demoWorkouts.length);
      localStorage.setItem(LS_SEEN_KEY, Date.now().toString());
      return;
    }

    api.calendar(today, future)
      .then((r) => {
        const published = r.workouts.filter(
          (w) => w.status === "published" && w.scheduled_date >= today,
        );
        setUpcomingPlans(published);
        // Planes con updated_at posterior al último vistazo = no vistos
        const unseen = published.filter((w) => new Date(w.updated_at).getTime() > seenAt);
        if (onUnseenCount) onUnseenCount(unseen.length);
        // Marcar como vistos ahora
        localStorage.setItem(LS_SEEN_KEY, Date.now().toString());
      })
      .catch(() => {});
  }, [onUnseenCount, demo, coachData]);

  const clean = code.trim().toUpperCase();

  async function preview() {
    if (!clean) return;
    setError(null);
    setWorking(true);
    try {
      const res = await api.previewCoachCode(clean);
      setCoachName(res.coachName);
      setStep("consenting");
    } catch {
      setError(t("my_coach_error_invalid"));
    } finally {
      setWorking(false);
    }
  }

  async function redeem() {
    setError(null);
    setWorking(true);
    try {
      const res = await api.redeemCoachCode(clean);
      if (res.ok) {
        setStep("done");
        load();
      } else {
        const errKey =
          res.error === "ya_tienes_coach" ? "my_coach_error_already" :
          res.error === "agotado" || res.error === "codigo_agotado" ? "my_coach_error_exhausted" :
          res.error === "caducado" || res.error === "codigo_caducado" ? "my_coach_error_expired" :
          res.error === "auto_vinculo" ? "my_coach_error_self" :
          "my_coach_error_invalid";
        setError(t(errKey));
        setStep("idle");
      }
    } catch {
      setError(t("my_coach_error_invalid"));
      setStep("idle");
    } finally {
      setWorking(false);
    }
  }

  async function revoke() {
    if (!confirm(t("my_coach_unlink_confirm"))) return;
    await api.revokeCoachLink();
    setCoach(null);
    setStep("idle");
    setCode("");
  }

  function consentBody() {
    return t("my_coach_consent_body").replace("{coachName}", coachName);
  }

  function formatPlanDate(d: string) {
    return new Date(`${d}T00:00:00`).toLocaleDateString(
      lang === "es" ? "es-ES" : "en-US",
      { weekday: "short", month: "short", day: "numeric" },
    );
  }

  if (coach === undefined) {
    return <div className="text-sm text-muted">{t("admin_loading")}</div>;
  }

  return (
    <section className="space-y-4 rounded-xl border border-line bg-panel/70 p-4">
      <h2 className="text-sm font-semibold uppercase tracking-wider text-muted">
        {t("my_coach_tab")}
      </h2>

      {coach ? (
        <>
          {/* Encabezado con info del coach */}
          <div className="flex items-center gap-3">
            {coach.profilePic ? (
              <img
                src={coach.profilePic}
                alt={coach.name}
                className="h-9 w-9 rounded-full object-cover"
              />
            ) : (
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-accent/20 text-sm font-semibold text-accent">
                {coach.name.slice(0, 1).toUpperCase()}
              </div>
            )}
            <div>
              <p className="text-[11px] uppercase tracking-wider text-muted">{t("my_coach_linked")}</p>
              <p className="font-semibold text-fg">{coach.name}</p>
            </div>
          </div>

          {/* Banner: sin fuente compartible */}
          {hasShareableSource === false && (
            <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-3 text-xs text-amber-700 dark:text-amber-400">
              {t("my_coach_no_shareable_banner")}
            </div>
          )}

          {/* Tabs: Mensajes / Próximos */}
          <div className="flex gap-1 border-b border-line pb-1">
            <button
              onClick={() => setCoachTab("msg")}
              className={`rounded-lg px-3 py-1 text-xs font-semibold transition ${
                coachTab === "msg" ? "bg-accent/15 text-accent" : "text-muted hover:text-fg"
              }`}
            >
              {t("msg_tab")}
            </button>
            <button
              onClick={() => setCoachTab("updates")}
              className={`rounded-lg px-3 py-1 text-xs font-semibold transition ${
                coachTab === "updates" ? "bg-accent/15 text-accent" : "text-muted hover:text-fg"
              }`}
            >
              {t("my_coach_updates")}
              {upcomingPlans.length > 0 && (
                <span className="ml-1 rounded-full bg-accent/20 px-1.5 text-[10px] font-bold text-accent">
                  {upcomingPlans.length}
                </span>
              )}
            </button>
          </div>

          {/* Chat con identidad del coach */}
          {coachTab === "msg" && (
            <DirectChat
              viewerRole="athlete"
              fetchMessages={
                demo && coachData
                  ? () => Promise.resolve({
                      messages: coachData.messages.map((m) => ({
                        id: m.id,
                        coach_user_id: "coach_demo",
                        athlete_user_id: "demo",
                        author_role: (m.sender === "user" ? "athlete" : "coach") as "coach" | "athlete",
                        body: m.body,
                        created_at: m.sent_at,
                        read_at: m.read ? m.sent_at : null,
                      })),
                    })
                  : () => api.coachMessages()
              }
              sendMessage={
                demo
                  ? () => Promise.resolve({ message: { id: "", coach_user_id: "", athlete_user_id: "", author_role: "coach", body: "", read_at: null, created_at: new Date().toISOString() } })
                  : (body) => api.sendCoachMessage(body)
              }
              peer={{ name: coach.name, profilePic: coach.profilePic }}
            />
          )}

          {/* Novedades: entrenamientos próximos */}
          {coachTab === "updates" && (
            <div className="space-y-2">
              {upcomingPlans.length === 0 ? (
                <p className="text-sm text-muted">{t("my_coach_no_updates")}</p>
              ) : (
                upcomingPlans.map((w) => (
                  <div
                    key={w.id}
                    className="flex items-center justify-between gap-2 rounded-xl border border-line bg-panel2/40 px-3 py-2"
                  >
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-fg">{w.payload.title}</p>
                      <p className="text-xs text-muted">{formatPlanDate(w.scheduled_date)}</p>
                    </div>
                    {onNavigateCalendar && (
                      <button
                        onClick={onNavigateCalendar}
                        className="shrink-0 rounded-lg border border-line px-2 py-1 text-xs text-muted transition hover:border-accent/40 hover:text-accent"
                      >
                        {t("my_coach_see_calendar")}
                      </button>
                    )}
                  </div>
                ))
              )}
            </div>
          )}

          {/* Unlink: zona discreta al final */}
          <div className="border-t border-line pt-3">
            <p className="mb-1 text-[11px] uppercase tracking-wider text-muted">
              {t("my_coach_unlink_section")}
            </p>
            <button
              onClick={() => void revoke()}
              className="text-xs text-muted underline-offset-2 transition hover:text-red-500 hover:underline"
            >
              {t("my_coach_unlink")}
            </button>
          </div>
        </>
      ) : step === "consenting" ? (
        /* Pantalla de consentimiento */
        <div className="max-w-md space-y-3">
          <p className="text-sm font-semibold text-fg">{t("my_coach_consent_title")}</p>
          <p className="text-sm text-muted">{consentBody()}</p>
          <div className="flex gap-2">
            <button
              onClick={() => void redeem()}
              disabled={working}
              className="rounded-lg bg-accent px-4 py-2 text-sm font-semibold text-white transition enabled:hover:brightness-110 disabled:opacity-40"
            >
              {t("my_coach_consent_accept")}
            </button>
            <button
              onClick={() => { setStep("idle"); setError(null); }}
              className="rounded-lg border border-line px-4 py-2 text-sm text-muted transition hover:text-fg"
            >
              {t("my_coach_consent_cancel")}
            </button>
          </div>
        </div>
      ) : (
        /* Sin coach: formulario de código */
        <div className="max-w-md space-y-3">
          <p className="text-sm text-muted">{t("my_coach_no_coach")}</p>
          <p className="text-sm font-medium text-fg">{t("my_coach_enter_code")}</p>
          <div className="flex gap-2">
            <input
              value={code}
              onChange={(e) => { setCode(e.target.value); setError(null); }}
              placeholder={lang === "es" ? "CCHXXXXXXX" : "CCHXXXXXXX"}
              className="h-9 flex-1 rounded-lg border border-line bg-panel px-3 text-sm text-fg outline-none focus:border-accent"
              onKeyDown={(e) => { if (e.key === "Enter") void preview(); }}
            />
            <button
              onClick={() => void preview()}
              disabled={!clean || working}
              className="h-9 rounded-lg bg-accent px-4 text-sm font-semibold text-white transition enabled:hover:brightness-110 disabled:opacity-40"
            >
              {t("my_coach_preview")}
            </button>
          </div>
          {error && <p className="text-xs text-red-500">{error}</p>}
        </div>
      )}
    </section>
  );
}
